/**
 * Interpretación del Score Ético — qué puede estar detrás del número.
 *
 * Principio de redacción: cada mensaje es una HIPÓTESIS, no un veredicto. El
 * score mide el desempeño del canal de denuncias, no la ética de la
 * organización. Un volumen bajo indica que no nos estamos enterando, no que no
 * esté pasando nada — y tampoco que la empresa esté actuando mal. Toda causa se
 * enuncia como "puede significar".
 *
 * Aquí vive sólo la lógica: qué estado aplica, qué observaciones se acumulan y
 * con qué severidad. Los textos están en las traducciones, bajo
 * `dashboard:static_dashboard.ethical_messages`, y los resuelve la UI.
 *
 *   const resultado = calcular(periodo);
 *   const lectura = interpretar(resultado, periodo, { mesesDesdeLanzamiento });
 */

import { PARAMETROS_DEFAULT } from "./ethicalScore";

// Estado principal, determinado por el volumen relativo al benchmark. La clave
// es también la clave de traducción bajo `ethical_messages.estados`.
export const ESTADOS = {
  silencio_critico: { severidad: "critica" },
  silencio: { severidad: "alta" },
  moderadamente_bajo: { severidad: "media" },
  saludable: { severidad: "ninguna" },
  elevado: { severidad: "media" },
  desbordado: { severidad: "critica" },
};

// Observaciones secundarias: se acumulan sobre el estado principal. Clave =
// clave de traducción bajo `ethical_messages.observaciones`.
export const OBSERVACIONES = {
  sustanciacion_baja: { severidad: "media" },
  sustanciacion_alta: { severidad: "media" },
  cierre_lento: { severidad: "alta" },
  represalias: { severidad: "critica" },
  modo_evento: { severidad: "media" },
  adopcion_temprana: { severidad: "baja" },
  denominador_inconsistente: { severidad: "alta" },
  poblacion_minima: { severidad: "alta" },
};

// De menor a mayor. `severidadMaxima` usa este orden para elegir el color del
// icono de alerta.
export const ORDEN_SEVERIDAD = ["ninguna", "baja", "media", "alta", "critica"];

const masSevera = (a, b) =>
  ORDEN_SEVERIDAD.indexOf(a) >= ORDEN_SEVERIDAD.indexOf(b) ? a : b;

// Observaciones que vuelven provisional el indicador: la base de cálculo no es
// confiable, así que el número se muestra con reserva.
const PROVISIONALES = ["denominador_inconsistente", "poblacion_minima"];

export function estadoPorVolumen(tasaAjustada, benchmark) {
  const ratio = tasaAjustada / benchmark;
  if (ratio < 0.15) return "silencio_critico";
  if (ratio < 0.5) return "silencio";
  if (ratio < 0.8) return "moderadamente_bajo";
  if (ratio <= 2.0) return "saludable";
  if (ratio <= 5.0) return "elevado";
  return "desbordado";
}

/**
 * @param {Object} resultado  salida de calcular()
 * @param {Object} periodo    los mismos datos de entrada
 * @param {Object} [opciones]
 * @param {number} [opciones.mesesDesdeLanzamiento]  suaviza la lectura si < 18
 * @param {Object} [opciones.parametros]
 * @returns {{estado: string, severidad: string, severidadMaxima: string,
 *            observaciones: Array<{clave: string, severidad: string}>,
 *            contexto: Object}}
 */
export function interpretar(resultado, periodo, opciones = {}) {
  const p = { ...PARAMETROS_DEFAULT, ...(opciones.parametros ?? {}) };
  const { mesesDesdeLanzamiento } = opciones;

  const claveEstado = estadoPorVolumen(resultado.tasaAjustada, p.benchmark);
  const estado = ESTADOS[claveEstado];

  const esperadas = Math.round((periodo.empleados / 100) * p.benchmark);
  const recibidas = periodo.casosUnicos;

  const claves = [];
  const agregar = (clave) => claves.push(clave);

  const enAdopcion = mesesDesdeLanzamiento != null && mesesDesdeLanzamiento < 18;
  if (enAdopcion && ["silencio", "silencio_critico"].includes(claveEstado)) {
    agregar("adopcion_temprana");
  }

  if (periodo.tasaSustanciacion != null) {
    if (periodo.tasaSustanciacion < 0.25) agregar("sustanciacion_baja");
    else if (periodo.tasaSustanciacion > 0.75) agregar("sustanciacion_alta");
  }

  if (
    periodo.diasMedianaCierre != null &&
    periodo.diasMedianaCierre > p.diasObjetivo * 1.5
  ) {
    agregar("cierre_lento");
  }

  if ((periodo.casosRepresalias ?? 0) > 0) agregar("represalias");

  if (
    periodo.reportesBrutos &&
    periodo.maxReportesMismoSujeto &&
    periodo.maxReportesMismoSujeto / periodo.reportesBrutos > 0.4
  ) {
    agregar("modo_evento");
  }

  if (
    periodo.denunciantesUnicos != null &&
    periodo.denunciantesUnicos > periodo.empleados
  ) {
    agregar("denominador_inconsistente");
  }

  if (periodo.empleados < 30) agregar("poblacion_minima");

  const observaciones = claves.map((clave) => ({
    clave,
    severidad: OBSERVACIONES[clave].severidad,
  }));

  return {
    estado: claveEstado,
    severidad: estado.severidad,
    severidadMaxima: observaciones.reduce(
      (acc, o) => masSevera(acc, o.severidad),
      estado.severidad
    ),
    observaciones,
    contexto: {
      esperadas,
      recibidas,
      brecha: esperadas - recibidas,
      tasaObservada: resultado.tasaAjustada,
      benchmark: p.benchmark,
      porcentajeDelReferente: Math.round(
        (resultado.tasaAjustada / p.benchmark) * 100
      ),
      provisional: claves.some((c) => PROVISIONALES.includes(c)),
    },
  };
}

export default interpretar;
